import React, { useState } from "react";
import { MButton } from "@/components/ui/atoms/MButtons";
import { MDatePicker } from "@/components/ui/molecules/MDatePicker";
import { MGender } from "@/components/ui/molecules/MGender";
import { MDialCode } from "@/components/ui/molecules/MDialCode";
import { MOtp } from "@/components/ui/molecules/MOtp";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";

const DrawerModulesTest = () => {
  // For multi-select DropDownDrawer demo
  const [isMultiDrawerOpen, setIsMultiDrawerOpen] = useState(false);
  const [multiSelected, setMultiSelected] = useState<string[]>([]);
  // For single-select DropDownDrawer demo
  const [isSingleDrawerOpen, setIsSingleDrawerOpen] = useState(false);
  const [singleSelected, setSingleSelected] = useState<string>("");

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isMonthYearPickerOpen, setIsMonthYearPickerOpen] = useState(false);

  const [isGenderPickerOpen, setIsGenderPickerOpen] = useState(false);
  const [isDialCodePickerOpen, setIsDialCodePickerOpen] = useState(false);
  const [isOtpPickerOpen, setIsOtpPickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedMonthYear, setSelectedMonthYear] = useState<Date | null>(null);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const [isTimePicker12Open, setIsTimePicker12Open] = useState(false);
  const [selectedTime12, setSelectedTime12] = useState<Date | null>(null);

  const [selectedGender, setSelectedGender] = useState<string | null>(null);
  const [selectedDialCode, setSelectedDialCode] = useState<string | null>(null);
  const [enteredOtp, setEnteredOtp] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);

  const handleOpenDatePicker = () => {
    setIsDatePickerOpen(true);
  };

  const handleCloseDatePicker = () => {
    setIsDatePickerOpen(false);
  };

  const handleDateSelected = (date: Date) => {
    setSelectedDate(date);
    setIsDatePickerOpen(false);
  };

  const handleOpenMonthYearPicker = () => {
    setIsMonthYearPickerOpen(true);
  };

  const handleCloseMonthYearPicker = () => {
    setIsMonthYearPickerOpen(false);
  };

  const handleMonthYearSelected = (date: Date) => {
    setSelectedMonthYear(date);
    setIsMonthYearPickerOpen(false);
  };

  const handleOpenGenderPicker = () => {
    setIsGenderPickerOpen(true);
  };

  const handleCloseGenderPicker = () => {
    setIsGenderPickerOpen(false);
  };

  const handleGenderSelected = (gender: string) => {
    setSelectedGender(gender);
    setIsGenderPickerOpen(false);
  };

  const handleOpenDialCodePicker = () => {
    setIsDialCodePickerOpen(true);
  };

  const handleCloseDialCodePicker = () => {
    setIsDialCodePickerOpen(false);
  };

  const handleDialCodeSelected = (code: string) => {
    setSelectedDialCode(code);
    setIsDialCodePickerOpen(false);
  };

  const handleOpenOtpPicker = () => {
    setIsOtpPickerOpen(true);
  };

  const handleCloseOtpPicker = () => {
    setIsOtpPickerOpen(false);
  };

  const handleOtpSubmitted = async (otp: string): Promise<string | true> => {
    setOtpLoading(true);
    // Immediate OTP verification (no delay)
    setEnteredOtp(otp);
    setIsOtpPickerOpen(false);
    setOtpLoading(false);
    return true;
  };

  const handleOtpResend = () => {
    setOtpLoading(true);
    // Simulate resend OTP
    setTimeout(() => {
      setOtpLoading(false);
    }, 1000);
  };

  const handleOtpEdit = () => {
    setIsOtpPickerOpen(false);
    // In a real app, this would navigate back to phone/email input
    console.log(
      "Edit button clicked - would navigate back to phone/email input"
    );
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatGender = (gender: string) => {
    const genderLabels: { [key: string]: string } = {
      male: "Male",
      female: "Female",
      "non-binary": "Non-binary",
      "prefer-not-to-say": "Prefer not to say",
      other: "Other",
    };
    return genderLabels[gender] || gender;
  };

  const formatDialCode = (code: string) => {
    const dialCodeLabels: { [key: string]: string } = {
      "+1": "+1 (USA/Canada)",
      "+91": "+91 (India)",
      "+44": "+44 (UK)",
      "+61": "+61 (Australia)",
      "+81": "+81 (Japan)",
      "+971": "+971 (UAE)",
      "+65": "+65 (Singapore)",
      "+49": "+49 (Germany)",
      "+33": "+33 (France)",
      "+86": "+86 (China)",
    };
    return dialCodeLabels[code] || code;
  };

  const handleOpenTimePicker = () => setIsTimePickerOpen(true);
  const handleCloseTimePicker = () => setIsTimePickerOpen(false);
  const handleTimeSelected = (date: Date) => {
    setSelectedTime(date);
    setIsTimePickerOpen(false);
  };

  function formatTime(date: Date) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  const handleOpenTimePicker12 = () => setIsTimePicker12Open(true);
  const handleCloseTimePicker12 = () => setIsTimePicker12Open(false);
  const handleTimeSelected12 = (date: Date) => {
    setSelectedTime12(date);
    setIsTimePicker12Open(false);
  };

  return (
    <div className="p-8 space-y-6 bg-[#F6F6F6] min-h-screen">
      <h1 className="text-2xl font-bold mb-6">Drawer Modules Test</h1>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Date of Birth Picker</h2>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <MButton variant="filled" size="md" onClick={handleOpenDatePicker}>
              Select Date of Birth
            </MButton>

            {selectedDate && (
              <div className="text-lg">
                <span className="font-medium">Selected: </span>
                <span className="text-blue-600">
                  {formatDate(selectedDate)}
                </span>
              </div>
            )}
          </div>

          {!selectedDate && (
            <p className="text-gray-500 text-sm">
              Click the button above to open the date picker
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">
          Month/Year Picker (hideDate=true)
        </h2>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <MButton
              variant="filled"
              size="md"
              onClick={handleOpenMonthYearPicker}
            >
              Select Month/Year
            </MButton>

            {selectedMonthYear && (
              <div className="text-lg">
                <span className="font-medium">Selected: </span>
                <span className="text-blue-600">
                  {formatDate(selectedMonthYear)}
                </span>
              </div>
            )}
          </div>

          {!selectedMonthYear && (
            <p className="text-gray-500 text-sm">
              This picker hides the date column and defaults to the 1st of the
              month
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Gender Picker</h2>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <MButton
              variant="filled"
              size="md"
              onClick={handleOpenGenderPicker}
            >
              Select Gender
            </MButton>

            {selectedGender && (
              <div className="text-lg">
                <span className="font-medium">Selected: </span>
                <span className="text-blue-600">
                  {formatGender(selectedGender)}
                </span>
              </div>
            )}
          </div>

          {!selectedGender && (
            <p className="text-gray-500 text-sm">
              Choose from predefined gender options
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Dial Code Picker</h2>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <MButton
              variant="filled"
              size="md"
              onClick={handleOpenDialCodePicker}
            >
              Select Country Code
            </MButton>

            {selectedDialCode && (
              <div className="text-lg">
                <span className="font-medium">Selected: </span>
                <span className="text-blue-600">
                  {formatDialCode(selectedDialCode)}
                </span>
              </div>
            )}
          </div>

          {!selectedDialCode && (
            <p className="text-gray-500 text-sm">
              Choose from available country dial codes
            </p>
          )}
        </div>
      </div>

      {/* OTP Picker */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-4">OTP Picker</h2>

        <div className="space-y-4">
          <div className="flex flex-col space-y-3">
            <MButton variant="filled" size="md" onClick={handleOpenOtpPicker}>
              Enter OTP
            </MButton>

            {enteredOtp && (
              <div className="text-lg">
                <span className="font-medium">Entered OTP: </span>
                <span className="text-green-600 font-mono">{enteredOtp}</span>
              </div>
            )}
          </div>

          {!enteredOtp && (
            <p className="text-gray-500 text-sm">
              Test the OTP input flow with resend functionality
            </p>
          )}
        </div>
      </div>

      {/* Drawer Modules */}
      <MDatePicker
        open={isDatePickerOpen}
        onClose={handleCloseDatePicker}
        onDone={handleDateSelected}
        title="Select Date of Birth"
        minYear={1980}
        maxDate={new Date(2028, 5, 15)}
      />

      <MDatePicker
        open={isMonthYearPickerOpen}
        onClose={handleCloseMonthYearPicker}
        onDone={handleMonthYearSelected}
        title="Select Month/Year"
        hideDate={true}
      />

      <MGender
        open={isGenderPickerOpen}
        onClose={handleCloseGenderPicker}
        onDone={handleGenderSelected}
        title="Select Gender"
      />

      <MDialCode
        open={isDialCodePickerOpen}
        onClose={handleCloseDialCodePicker}
        onDone={handleDialCodeSelected}
        title="Select Country Code"
      />

      <MOtp
        open={isOtpPickerOpen}
        onClose={handleCloseOtpPicker}
        onDone={handleOtpSubmitted}
        onResend={handleOtpResend}
        onEdit={handleOtpEdit}
        title="Enter 6-digit Code"
        sendTo="test@example.com"
        loading={otpLoading}
      />
      {/* Single-select DropDown Drawer Test */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Single-select DropDown Drawer</h2>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <MButton
              variant="filled"
              size="md"
              onClick={() => setIsSingleDrawerOpen(true)}
            >
              Open Single-select Drawer
            </MButton>
            {singleSelected && (
              <div className="text-lg">
                <span className="font-medium">Selected: </span>
                <span className="text-blue-600">
                  {[
                    { value: "apple", label: "Apple" },
                    { value: "banana", label: "Banana" },
                    { value: "orange", label: "Orange" },
                    { value: "grape", label: "Grape" },
                    { value: "mango", label: "Mango" },
                  ].find((opt) => opt.value === singleSelected)?.label ||
                    singleSelected}
                </span>
              </div>
            )}
          </div>
          {!singleSelected && (
            <p className="text-gray-500 text-sm">
              Select one option from the drawer below
            </p>
          )}
        </div>
      </div>

      <MDropDownDrawer
        open={isSingleDrawerOpen}
        onClose={() => setIsSingleDrawerOpen(false)}
        onSelect={(val) => {
          if (typeof val === "string") setSingleSelected(val);
        }}
        title="Select One Fruit"
        items={[
          { value: "apple", label: "Apple" },
          { value: "banana", label: "Banana" },
          { value: "orange", label: "Orange" },
          { value: "grape", label: "Grape" },
          { value: "mango", label: "Mango" },
        ]}
        variant="single"
        initialValue={singleSelected}
      />

      {/* Multi-select DropDown Drawer Test */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Multi-select DropDown Drawer</h2>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <MButton
              variant="filled"
              size="md"
              onClick={() => setIsMultiDrawerOpen(true)}
            >
              Open Multi-select Drawer
            </MButton>
            {multiSelected.length > 0 && (
              <div className="text-lg">
                <span className="font-medium">Selected: </span>
                <span className="text-blue-600">
                  {multiSelected.join(", ")}
                </span>
              </div>
            )}
          </div>
          {multiSelected.length === 0 && (
            <p className="text-gray-500 text-sm">
              Select multiple options from the drawer below
            </p>
          )}
        </div>
      </div>

      <MDropDownDrawer
        open={isMultiDrawerOpen}
        onClose={() => setIsMultiDrawerOpen(false)}
        onSelect={(val) => {
          if (Array.isArray(val)) setMultiSelected(val);
        }}
        title="Select Multiple Fruits"
        items={[
          { value: "apple", label: "Apple" },
          { value: "banana", label: "Banana" },
          { value: "orange", label: "Orange" },
          { value: "grape", label: "Grape" },
          { value: "mango", label: "Mango" },
        ]}
        variant="multi"
        selectedValues={multiSelected}
      />
    </div>
  );
};

export default DrawerModulesTest;
